//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-day imports
import { IProps, IState, IContainer } from "./ChromeDayPicker.d";
import * as s from "./ChromeDayPicker.styles";
import { fragments } from "./ChromeDayPicker.gql";
import { AvatarPerson } from "@components/avatar";
import { FocusTimeMeter } from "@components/focus-time-meter";
// util
import { track, TrackingEvents } from "@util/analytics.util";
import { ZonedMoment } from "@util/time-zone.util";
import { IReduxState } from "@reducers/root.reducer";
import { GOAL_MINUTES } from "@util/team-health.util";
// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
// material-ui
import { Button, IconButton, InputAdornment } from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { ArrowDropDown, ChevronLeft, ChevronRight } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
export class ChromeDayPickerCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      datePickerVisible: false,
    };
  }

  private onDateChange = (zonedDate: ZonedMoment) => {
    this.props.setSelectedMoment(zonedDate.clone());
  };

  private onSelectDate = (muiDate: MaterialUiPickersDate) => {
    const date = muiDate ? muiDate.toDate() : new Date();
    // convert to local date first because... timezones
    const zonedDate = new ZonedMoment(date.toISOString().split("T")[0]);

    this.onDateChange(zonedDate);

    track(TrackingEvents.DAY_VIEW.PICK_DATE);
  };

  private onDateChangeNavigation = (forward: boolean) => {
    const nextDate = this.props.selectedMoment.clone().add(forward ? 1 : -1, "days");

    this.props.setSelectedMoment(nextDate);
    track(forward ? TrackingEvents.DAY_VIEW.PICK_NEXT_DAY : TrackingEvents.DAY_VIEW.PICK_PREV_DAY);
  };

  private onSelectToday = () => {
    this.onDateChange(new ZonedMoment());
    if (this.props.scrollToNow) {
      this.props.scrollToNow();
    }
    track(TrackingEvents.DAY_VIEW.PICK_TODAY);
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  public render() {
    const { org, calendarFocusTimeScore, loading } = this.props;
    let backupCalendarId = "";
    let personForUser = null;
    if (org && org.personForUserErrorable && org.personForUserErrorable.__typename === "Person") {
      personForUser = org.personForUserErrorable;
      backupCalendarId = org.primaryOrgEmail;
    }

    return (
      <div className={this.props.classes.dayPickerContainer}>
        <div className={this.props.classes.dayPickerTop}>
          <DatePicker
            id="day-date-picker"
            onChange={this.onSelectDate}
            value={this.props.selectedMoment.toDate()}
            variant="inline"
            autoOk
            disableToolbar
            format="ddd MMM D"
            style={s.styles.dayPicker}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: this.props.classes.dayPickerTextField,
                input: this.props.classes.dayPickerTextFieldInput,
              },
              endAdornment: (
                <InputAdornment position="start">
                  <ArrowDropDown htmlColor={greys.slate} style={{ verticalAlign: "middle" }} />
                </InputAdornment>
              ),
            }}
          />
          <div className={this.props.classes.dayPickerChevronContainer}>
            <IconButton
              style={{ padding: "12px 8px" }}
              onClick={() => this.onDateChangeNavigation(false)}
            >
              <ChevronLeft htmlColor={greys.slate} />
            </IconButton>
            <IconButton
              style={{ padding: "12px 8px" }}
              onClick={() => this.onDateChangeNavigation(true)}
            >
              <ChevronRight htmlColor={greys.slate} />
            </IconButton>
          </div>
          <div className={this.props.classes.dayPickerTodayButtonContainer}>
            <Button
              onClick={this.onSelectToday}
              style={s.styles.dayPickerTodayButton}
              variant="outlined"
            >
              Today
            </Button>
          </div>
        </div>
        {org && personForUser && calendarFocusTimeScore && !loading ? (
          <div className={this.props.classes.dayPickerBottom}>
            <div className={this.props.classes.dayPickerAvatar}>
              <AvatarPerson
                org={org}
                person={personForUser}
                orgPerson={null}
                size={36}
                backupCalendarId={backupCalendarId}
                parentName={"ChromeDayPicker"}
              />
            </div>
            <FocusTimeMeter
              busyMinutes={calendarFocusTimeScore.busyMinutes}
              focusMinutes={calendarFocusTimeScore.focusMinutes}
              totalMinutes={calendarFocusTimeScore.totalMinutes}
              goalMinutes={GOAL_MINUTES / 5}
              crawledStatus={calendarFocusTimeScore.crawledStatus}
              noLabel={true}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////
const ChromeDayPickerStyled = withStyles(s.styles)(ChromeDayPickerCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {})(ChromeDayPickerStyled);

//////////////////
// RELAY
//////////////////
export const ChromeDayPicker = createFragmentContainer(reduxContainer, fragments);
